import Delete from '@mui/icons-material/Delete';
import Edit from '@mui/icons-material/Edit';
import MoreHoriz from '@mui/icons-material/MoreHoriz';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import { styled } from '@mui/material/styles';
import Typography, { TypographyOwnProps } from '@mui/material/Typography';
import { AddOnRateType, AddOnType } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';

import actionsColumn from '~components/DataTable/actionsColumn';
import { DataTableColumn } from '~components/DataTable/dataTableColumn';
import { SimpleMenu } from '~components/Menu/SimpleMenu';
import { AddOn } from '~hooks/useAddOns/models';
import { useCompanyCurrency } from '~hooks/useCompanyCurrency/useCompanyCurrency';
import { formatPercentage } from '~utils/utilFunctions';

const CellText = styled(Typography)<TypographyOwnProps>(() => ({
  '&.MuiTypography-root': {
    fontSize: '12px',
  },
}));

export const rateTypeTextByAddOnRateType: Record<AddOnRateType, string> = {
  [AddOnRateType.RATE_FOR_EACH]: t('administration.rates.add_ons.rate_for_each'),
  [AddOnRateType.RATE_PERCENT_OF_TOTAL]: t(
    'administration.rates.add_ons.rate_percent_of_total',
  ),
  [AddOnRateType.PER_LOAD]: t('administration.rates.add_ons.per_load'),
  [AddOnRateType.FUEL_SURCHARGE]: t('administration.rates.add_ons.fuel_surcharge'),
};

const addOnTypeTextByAddOnType: Record<AddOnType, string> = {
  [AddOnType.DRIVER_PAY]: t('administration.rates.add_ons.add_on_type_driver_pay'),
  [AddOnType.INVOICING]: t('administration.rates.add_ons.add_on_type_invoicing'),
  [AddOnType.JOB]: t('administration.rates.add_ons.add_on_type_job'),
  [AddOnType.SETTLEMENT]: t('administration.rates.add_ons.add_on_type_settlement'),
  [AddOnType.DRIVER_DAY]: t('administration.rates.add_ons.add_on_type_driver_day'),
};

const formatAddOnTypes = (addOn: AddOn) => {
  const addOnTypes = addOn.addOnTypes.map((type) => addOnTypeTextByAddOnType[type]);

  return addOnTypes.sort().join(', ');
};

type CurrencyFormatterFn = ReturnType<typeof useCompanyCurrency>['currencyFormatter'];

type AddOnChargesRowDefUtils = {
  canDeleteAddOn: boolean;
  currencyFormatter: CurrencyFormatterFn;
  onDeleteButtonClick: (addOn: AddOn) => void;
  onEditButtonClick: (addOn: AddOn) => void;
};

export type AddOnChargesRow = {
  id: string;
  addOn: AddOn;
  utils: AddOnChargesRowDefUtils;
};

type AddOnChargesColumn = DataTableColumn<AddOnChargesRow>;

export const descriptionColDef: AddOnChargesColumn = {
  field: 'name',
  headerName: t('common.name'),
  renderCell: (row) => {
    return <CellText>{row.addOn.name}</CellText>;
  },
};

export const invoiceRateColDef: AddOnChargesColumn = {
  field: 'rate',
  headerName: t('administration.rates.invoice_rate'),
  renderCell: (row) => {
    if (row.addOn.fuelSurchargeSchedule) {
      return <CellText></CellText>;
    }

    if (row.addOn.revenueCostRateType === AddOnRateType.RATE_PERCENT_OF_TOTAL) {
      return <CellText>{formatPercentage(row.addOn.percentage)}</CellText>;
    }

    return <CellText>{row.utils.currencyFormatter(row.addOn.rate ?? 0)}</CellText>;
  },
};

export const rateTypeColDef: AddOnChargesColumn = {
  field: 'revenueCostRateType',
  headerName: t('administration.rates.rate_type'),
  renderCell: (row) => {
    return (
      <CellText>{rateTypeTextByAddOnRateType[row.addOn.revenueCostRateType]}</CellText>
    );
  },
};

export const addOnTypesColDef: AddOnChargesColumn = {
  field: 'addOnTypes',
  headerName: t('administration.rates.add_ons.add_on_types'),
  renderCell: (row) => {
    return <CellText>{formatAddOnTypes(row.addOn)}</CellText>;
  },
};

export const externalIdColDef: AddOnChargesColumn = {
  field: 'externalId',
  headerName: t('form_fields.external_id'),
  renderCell: (row) => {
    return <CellText>{row.addOn.externalId || ''}</CellText>;
  },
};

export const accountingIdColDef: AddOnChargesColumn = {
  field: 'accountingId',
  headerName: t('form_fields.accounting_id'),
  renderCell: (row) => {
    return <CellText>{row.addOn.accountingId || ''}</CellText>;
  },
};

export const quantityColDef: AddOnChargesColumn = {
  field: 'quantity',
  headerName: t('administration.rates.add_ons.default_quantity'),
  renderCell: (row) => {
    if (row.addOn.fuelSurchargeSchedule) {
      return <CellText></CellText>;
    }

    return <CellText>{row.addOn.quantity}</CellText>;
  },
};

export const costRateColDef: AddOnChargesColumn = {
  field: 'costRateValue',
  headerName: t('administration.rates.add_ons.cost_rate'),
  renderCell: (row) => {
    if (row.addOn.fuelSurchargeSchedule) {
      return <CellText></CellText>;
    }

    if (row.addOn.revenueCostRateType === AddOnRateType.RATE_PERCENT_OF_TOTAL) {
      return <CellText>{formatPercentage(row.addOn.costRateValue ?? 0)}</CellText>;
    }
    return (
      <CellText>{row.utils.currencyFormatter(row.addOn.costRateValue ?? 0)}</CellText>
    );
  },
};

export const fuelSurchargeScheduleColDef: AddOnChargesColumn = {
  field: 'fuelSurchargeSchedule',
  headerName: t('administration.rates.add_ons.fuel_surcharge_schedule'),
  renderCell: (row) => {
    return <CellText>{row.addOn.fuelSurchargeSchedule?.name || ''}</CellText>;
  },
};

export const actionsColDef = (): AddOnChargesColumn =>
  actionsColumn((row) => {
    const deleteOption = row.utils.canDeleteAddOn
      ? [
          {
            title: t('administration.rates.add_ons.delete_add_on'),
            icon: <Delete />,
            callBack: () => row.utils.onDeleteButtonClick(row.addOn),
          },
        ]
      : [];

    return (
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <IconButton
          size="small"
          onClick={() => row.utils.onEditButtonClick(row.addOn)}
          data-ui={`page-settings__rates__add-on-charges__row-${row.addOn.id}__edit-button`}
        >
          <Edit />
        </IconButton>
        {deleteOption.length > 0 && (
          <SimpleMenu options={deleteOption}>
            <MoreHoriz />
          </SimpleMenu>
        )}
      </Box>
    );
  });
